import MainURL from '../../../../MainURL';
import ImageURL from '../../../../ImageURL';
import type { CareerItem, EducationItem } from '../../../../types/application';
import type { PortfolioItem } from '../ministerInfo/ministerInfoTypes';
import { portfolioImageSrc } from '../ministerInfo/ministerInfoUtils';

export interface TimelineItem {
  year: string;
  content: string[];
}

// 프로필페이지 — 공통 데이터(이름·연락처·사진·교회·교단·직분·학력·경력·포트폴리오) + 프로필페이지 전용 데이터
// (server/routers/minister/MinisterProfile.js buildProfile)
export interface MinisterProfile {
  id: string;
  isPublic: boolean;
  mainImage: string;
  sort: string;
  subNameSort: string;
  ministryFields: string[];
  timeline: TimelineItem[];
  showPhone: boolean;
  showEmail: boolean;
  name: string;
  profileImage: string;
  church: string;
  religiousbody: string;
  dutyTitle: string;
  educationItems: EducationItem[];
  careerItems: CareerItem[];
  portfolioItems: PortfolioItem[];
  phone: string;
  email: string;
  updatedAt: string;
  isOwner?: boolean;
}

export interface MinisterProfileListItem {
  id: string;
  name: string;
  mainImage: string;
  profileImage: string;
  church: string;
  religiousbody: string;
  dutyTitle: string;
  sort: string;
  subNameSort: string;
  ministryFields: string[];
}

// 이름 아래 한 줄 — '구분'을 고르면 구분, 아니면 직분
export const profileSubTitle = (item: { subNameSort: string; sort: string; dutyTitle: string }) =>
  item.subNameSort === '구분' ? item.sort : item.dutyTitle;

// 대표사진(가로) — 없으면 증명사진
export const profileMainImageSrc = (item: { mainImage: string; profileImage: string }) => {
  if (item.mainImage) return `${ImageURL}/images/resume/profilemain/${item.mainImage}`;
  if (item.profileImage) return `${ImageURL}/images/resume/profile/${item.profileImage}`;
  return '';
};

export const profilePhotoSrc = (item: { mainImage: string; profileImage: string }) => {
  if (item.profileImage) return `${ImageURL}/images/resume/profile/${item.profileImage}`;
  return profileMainImageSrc(item);
};

export const profileImageFileSrc = (fileName: string) => portfolioImageSrc(fileName);

const yearOf = (date: string) => (String(date || '').match(/\d{4}/) || [''])[0];

// 연혁 — 직접 쓴 연혁이 없으면 경력·학력으로 만든다 (최근 연도 먼저)
export function buildTimeline(profile: Pick<MinisterProfile, 'timeline' | 'careerItems' | 'educationItems'>): TimelineItem[] {
  if (profile.timeline.length > 0) return profile.timeline;
  const entries = [
    ...profile.careerItems.map((c) => ({
      year: yearOf(c.startDate),
      text: [c.companyName, c.position].filter(Boolean).join(' '),
    })),
    ...profile.educationItems.map((e) => ({
      year: yearOf(e.startDate),
      text: [e.schoolName, e.major, e.status].filter(Boolean).join(' '),
    })),
  ].filter((entry) => entry.text);

  const byYear = new Map<string, string[]>();
  entries.forEach(({ year, text }) => byYear.set(year, [...(byYear.get(year) || []), text]));
  return [...byYear.entries()]
    .sort(([a], [b]) => (b || '0').localeCompare(a || '0'))
    .map(([year, content]) => ({ year, content }));
}

// 프로필 고유 주소 — 목록·상세의 '링크 복사'
export const profileShareUrl = (id: string) =>
  `https://www.ministermore.co.kr/minister/profilepage/detail?id=${encodeURIComponent(id)}`;

export const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // clipboard API 미지원(구형 브라우저/웹뷰) 폴백
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
};
